import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { portfolioData } from '../mock';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = portfolioData.projects.find(p => p.id === parseInt(id));
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [lightboxOpen]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (project.gallery) {
      setCurrentImageIndex((prev) => (prev + 1) % project.gallery.length);
    }
  };

  const prevImage = () => {
    if (project.gallery) {
      setCurrentImageIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    }
  };

  if (!project) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Project Not Found</h1>
          <Link to="/work" className="text-amber-400 hover:text-amber-300">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Lightbox */}
      {lightboxOpen && project.gallery && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all duration-300 z-10"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Previous Button */}
          {project.gallery.length > 1 && (
            <button
              onClick={prevImage}
              className="absolute left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all duration-300"
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Image */}
          <div className="max-w-7xl max-h-[90vh] px-20">
            <img
              src={project.gallery[currentImageIndex]}
              alt={`${project.title} - Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="text-center mt-4 text-gray-400">
              {currentImageIndex + 1} / {project.gallery.length}
            </div>
          </div>

          {/* Next Button */}
          {project.gallery.length > 1 && (
            <button
              onClick={nextImage}
              className="absolute right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all duration-300"
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* Click outside to close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={closeLightbox}
          />
        </div>
      )}

      {/* Header */}
      <section className="py-12 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-amber-400 transition-colors duration-300 mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to Work</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/10 border border-amber-400/20 rounded-full">
                  <Tag size={16} className="text-amber-400" />
                  <span className="text-amber-400 text-sm font-semibold">{project.category}</span>
                </span>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-gray-400 text-sm font-semibold">{project.year}</span>
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {project.title}
              </h1>
              <p className="text-xl text-gray-400 max-w-3xl">
                {project.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {project.gallery && project.gallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {project.gallery.map((image, index) => (
                <div
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="group relative overflow-hidden rounded-2xl bg-black border border-white/5 hover:border-amber-400/30 transition-all duration-500 cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={image}
                      alt={`${project.title} - Image ${index + 1}`}
                      className="w-full h-full object-contain bg-zinc-900 group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="text-white text-sm font-semibold bg-amber-400/20 border border-amber-400 px-4 py-2 rounded-full">
                      Click to Enlarge
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400">No gallery images available for this project.</p>
            </div>
          )}
        </div>
      </section>

      {/* Project Info */}
      <section className="py-24 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-12">
            <h2 className="text-3xl font-bold text-white mb-6">Project Details</h2>
            <div className="space-y-4 text-gray-300">
              <div className="flex items-start gap-4">
                <span className="font-semibold text-amber-400 min-w-[120px]">Category:</span>
                <span>{project.category}</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="font-semibold text-amber-400 min-w-[120px]">Year:</span>
                <span>{project.year}</span>
              </div>
              <div className="flex items-start gap-4">
                <span className="font-semibold text-amber-400 min-w-[120px]">Description:</span>
                <span>{project.description}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Project */}
      <section className="py-12 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 hover:border-amber-400/50 transition-all duration-300"
          >
            View All Projects
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
